import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDownWideShort, faArrowUpWideShort } from '@fortawesome/pro-regular-svg-icons';
import { useBidsSearchArgs } from 'src/reactiveVars';
import { useUpdateWsSorting } from 'src/models/bids/Bids/hooks';
import { BidsColumn, SORTABLE_KANBAN_BIDS_COLUMNS, SortOrder } from 'src/models/bids/Bids/types';
import PlainDropDown from 'src/common/PlainDropDown';
import { trackWorkspaceKanbanSorting } from 'src/segment/events';
import styles from './index.module.scss';

interface Props {
  wsId: string;
}

const BidsSorting: FC<Props> = ({ wsId }) => {
  const { t } = useTranslation();
  const [{ sortOnField }] = useBidsSearchArgs();
  const [updateBidsSorting] = useUpdateWsSorting();

  const onChangeSortingName = useCallback(
    (item: BidsColumn) => {
      if (sortOnField) {
        updateBidsSorting({ ...sortOnField, name: item });

        trackWorkspaceKanbanSorting({ id: wsId }, item);
      }
    },
    [sortOnField, wsId, updateBidsSorting]
  );

  const currentOrderIsAsc = sortOnField?.order === SortOrder.ASC;
  const onChangeSortingOrder = useCallback(() => {
    sortOnField && updateBidsSorting({ ...sortOnField, order: currentOrderIsAsc ? SortOrder.DESC : SortOrder.ASC });
  }, [currentOrderIsAsc, sortOnField, updateBidsSorting]);

  const values = useMemo(
    () =>
      SORTABLE_KANBAN_BIDS_COLUMNS.map(name => {
        const label = Object.keys(BidsColumn).find(key => BidsColumn[key as keyof typeof BidsColumn] === name);
        return {
          value: name,
          label: label ? t(`BidSpaces.BidsSort.${label}`) : undefined
        };
      }),
    [t]
  );

  return (
    <div className={styles.container}>
      <PlainDropDown
        values={values}
        selectedValue={sortOnField?.name}
        label={t('Common.sortBy')}
        onSelect={onChangeSortingName}
        type="single"
      />
      <button className={styles.button} onClick={onChangeSortingOrder}>
        <FontAwesomeIcon icon={currentOrderIsAsc ? faArrowDownWideShort : faArrowUpWideShort} className={styles.icon} />
      </button>
    </div>
  );
};

export default BidsSorting;
